import {
  type MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  SearchUtils,
  extractLastNameComponent,
  extractWorkflowFromInstanceName,
} from '@spotify/confidence-core-react';
import {
  type ConfidenceResourceSearchQuery,
  type PulsesQuery,
  getTypeOrNull,
  useConfidenceResourceSearchQuery,
  usePulsesQuery,
} from '@spotify/confidence-graphql';
import {
  WORKFLOW_TYPE_IDS,
  formatWorkflowDisplayName,
} from '@spotify/plugin-confidence-workflows-react/workflowDisplayNames';

import {
  LOADING_SENTINEL,
  type MentionItem,
  NO_RESULTS_SENTINEL,
} from '../../components/ChatInput/MentionSuggestion';
import { createMentionExtension } from '../../components/ChatInput/mentionExtension';

export const DEFAULT_WORKFLOW_TYPE_IDS = WORKFLOW_TYPE_IDS;
const MENTION_RESULTS_PER_TYPE = 5;
const MENTION_SEARCH_PAGE_SIZE =
  MENTION_RESULTS_PER_TYPE * DEFAULT_WORKFLOW_TYPE_IDS.length;

export const buildWorkflowParentFilter = (
  workflowTypeIds: readonly string[],
): string => workflowTypeIds.map(id => `parent:"workflows/${id}"`).join(' OR ');

export const buildMentionFilters = (
  query: string,
  workflowTypeIds: readonly string[] = DEFAULT_WORKFLOW_TYPE_IDS,
): {
  displayNameFilter: string | undefined;
  nameFilter: string | undefined;
  workflowFilter: string;
} => {
  const displayNameFilter =
    SearchUtils.buildSearchFilter(query, 'displayName') || undefined;
  const nameFilter = query
    ? `name:*${SearchUtils.escapeQuery(query)}*`
    : undefined;
  const parentFilter = buildWorkflowParentFilter(workflowTypeIds);
  const workflowFilter = displayNameFilter
    ? `(${parentFilter}) ${displayNameFilter}`
    : `(${parentFilter})`;
  return { displayNameFilter, nameFilter, workflowFilter };
};

type UseMentionSearchResult = {
  mentionExtension: ReturnType<typeof createMentionExtension>;
  mentionOpenRef: MutableRefObject<boolean>;
};

const groupWorkflowsByType = <Workflow extends { name: string }>(
  workflows: readonly Workflow[],
): Workflow[] => {
  const workflowGroups = new Map<string, Workflow[]>();

  for (const workflow of workflows) {
    const workflowTypeId =
      extractWorkflowFromInstanceName(workflow.name) ?? workflow.name;
    const workflowGroup = workflowGroups.get(workflowTypeId);

    if (workflowGroup) {
      if (workflowGroup.length < MENTION_RESULTS_PER_TYPE) {
        workflowGroup.push(workflow);
      }
    } else {
      workflowGroups.set(workflowTypeId, [workflow]);
    }
  }

  return Array.from(workflowGroups.values()).flat();
};

export const buildMentionItems = (
  data: ConfidenceResourceSearchQuery | undefined,
  pulsesData?: PulsesQuery,
): MentionItem[] => {
  const items: MentionItem[] = [];

  const workflows = getTypeOrNull(data?.workflowInstances)?.workflowInstances;
  if (workflows) {
    for (const workflow of groupWorkflowsByType(workflows)) {
      items.push({
        id: workflow.name,
        label:
          workflow.displayName ??
          extractLastNameComponent(workflow.name) ??
          workflow.name,
        typeLabel: formatWorkflowDisplayName(
          extractWorkflowFromInstanceName(workflow.name) ?? 'workflow',
          { capitalize: true },
        ),
      });
    }
  }

  const metrics = getTypeOrNull(data?.metrics)?.metrics;
  if (metrics) {
    for (const metric of metrics.slice(0, MENTION_RESULTS_PER_TYPE)) {
      items.push({
        id: metric.name,
        label:
          metric.displayName ??
          extractLastNameComponent(metric.name) ??
          metric.name,
        typeLabel: 'Metric',
      });
    }
  }

  const flags = getTypeOrNull(data?.flags)?.flags;
  if (flags) {
    for (const flag of flags.slice(0, MENTION_RESULTS_PER_TYPE)) {
      items.push({
        id: flag.name,
        label: extractLastNameComponent(flag.name) ?? flag.name,
        typeLabel: 'Flag',
      });
    }
  }

  const pulses = getTypeOrNull(pulsesData?.pulses)?.pulses;
  if (pulses) {
    for (const pulse of pulses.slice(0, MENTION_RESULTS_PER_TYPE)) {
      items.push({
        id: pulse.name,
        label:
          pulse.displayName ??
          extractLastNameComponent(pulse.name) ??
          pulse.name,
        typeLabel: 'Pulse',
        secondaryLabel: getTypeOrNull(pulse.owner)?.displayName,
      });
    }
  }

  return items;
};

export const useMentionSearch = (): UseMentionSearchResult => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(false);

  const { displayNameFilter, nameFilter, workflowFilter } = useMemo(
    () => buildMentionFilters(query),
    [query],
  );

  const { data, loading: resourcesLoading } = useConfidenceResourceSearchQuery({
    fetchPolicy: 'no-cache',
    variables: {
      filter: displayNameFilter,
      nameFilter,
      workflowFilter,
      pageSize: MENTION_RESULTS_PER_TYPE,
      workflowPageSize: MENTION_SEARCH_PAGE_SIZE,
      includeWorkflows: true,
      includeMetrics: true,
      includeFlags: true,
      includeFactTables: false,
      includeAssignmentTables: false,
      includeEvents: false,
      includeEntities: false,
      includeInitiatives: false,
      includeProjects: false,
      includeSurfaces: false,
      includePulses: false,
    },
    skip: !active,
  });

  const { data: pulsesData, loading: pulsesLoading } = usePulsesQuery({
    fetchPolicy: 'no-cache',
    variables: {
      filter: displayNameFilter,
      pageSize: MENTION_RESULTS_PER_TYPE,
    },
    skip: !active,
  });

  const loading = resourcesLoading || pulsesLoading;

  const results = useMemo(
    () => buildMentionItems(data, pulsesData),
    [data, pulsesData],
  );

  const suggestionsRef = useRef<MentionItem[]>([LOADING_SENTINEL]);
  const forceUpdatePopupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (loading) {
      suggestionsRef.current = [LOADING_SENTINEL];
    } else {
      suggestionsRef.current =
        results.length > 0 ? results : [NO_RESULTS_SENTINEL];
    }
    forceUpdatePopupRef.current?.();
  }, [results, loading]);

  const mentionOpenRef = useRef(false);

  const mentionExtension = useMemo(
    () =>
      createMentionExtension(
        () => suggestionsRef.current,
        open => {
          mentionOpenRef.current = open;
          setActive(open);
          if (!open) {
            setQuery('');
            forceUpdatePopupRef.current = null;
          }
        },
        q => setQuery(q),
        undefined,
        update => {
          forceUpdatePopupRef.current = update;
        },
      ),
    [],
  );

  return { mentionExtension, mentionOpenRef };
};
